import { ChangeDetectorRef, Component, ElementRef, HostListener, Input, ViewChild, NgZone, Output, EventEmitter } from '@angular/core';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { CvNumberLocalizePipe } from 'src/app/reusables/pipes/cv-number-localize.pipe';
import { GamesDataByLogin } from 'src/app/dashboard/modules/cxone-games/game.models';
import { GamesService } from 'src/app/gamification/command-center/services/games.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { SupervisorService } from '../supervisor.service';
import { TooltipDirective } from 'ngx-bootstrap/tooltip';
import { ViewType } from '../supervisor.model';
import OverlayScrollbars from 'overlayscrollbars';
import { NavigationService } from 'src/app/navigation/navigation.service';

@Component({
    selector: 'cxone-see-more',
    templateUrl: './cxone-see-more.component.html',
    styleUrl: './cxone-see-more.component.scss'
})
export class CxoneSeeMoreComponent {
    scrollSize = 0;
    isWidgetSmall = true;
    noData = false;
    private resizeTimeout;
    moduleManager: ModuleManager;
    isWidgetLarge = false;
    isGameContainer = false;

    @Input() moduleKey: string;
    @Input() agent: any;
    @Output() agentUpdated = new EventEmitter<any>();
    @Output() toggleSeeMore = new EventEmitter<boolean>();
    
    @ViewChild("seeCardContainer") seeCardContainerRef: ElementRef;
    @ViewChild("header") headerRef: ElementRef;
    @ViewChild('agentText') agentText: ElementRef<HTMLElement>;
    @ViewChild('tooltipAgent', { static: false }) tooltipAgent!: TooltipDirective;
    @ViewChild('agentTeamText') agentTeamText: ElementRef<HTMLElement>;
    @ViewChild('totalGames') totalGames: ElementRef<HTMLElement>;
    @ViewChild('totalCoins') totalCoins: ElementRef<HTMLElement>;
    @ViewChild('totalGamesLabel') totalGamesLabel: ElementRef<HTMLElement>;
    @ViewChild('totalCoinsLabel') totalCoinsLabel: ElementRef<HTMLElement>;
    @ViewChild('totalGamesContainer') totalGamesContainer: ElementRef<HTMLElement>;

    public isDataLoading = false;
    public somethingWentWrong = false;
    public gamesData: GamesDataByLogin;
    public gamesPromise = Promise.resolve("Simulated successful data");
    public viewType = ViewType;
    private resizeObserver!: ResizeObserver;
    maxWith = 0;
    maxWithWidget = 320;
    public noPMLicense = false;
    private loginId = 0;

    scrollOptions: OverlayScrollbars.PartialOptions = {
        scrollbars: {
            autoHide: "leave",
            autoHideDelay: 100,
            theme: "scrollbar-challenge",
        },
    };

    hiddenScrollOptions: OverlayScrollbars.PartialOptions = {
        scrollbars: {
            autoHide: "leave",
            visibility: "hidden",
            autoHideDelay: 100,
            theme: "scrollbar-challenge",
        },
    };

    constructor(
        public cvLocalizationService: CVLocalizationService,
        public cvNumberLocalizePipe: CvNumberLocalizePipe,
        private supervisorService: SupervisorService,
        private gamesService: GamesService,
        private cdr: ChangeDetectorRef,
        private elRef: ElementRef,
        private zone: NgZone,
        private navigationService: NavigationService
    ) {}

    async ngOnInit() {
        this.cdr.detectChanges();
        this.supervisorService.getModuleManager()?.subscribe(modulemanager => {
            this.moduleManager = modulemanager;
        });
        await this.loadLocalization();
        this.moduleManager?.registerEvents({
            onResize: () => this.handleResizeEvent()
        });

        this.moduleManager.showLoader = false;
        this.moduleManager.setOptions({
            onRefresh: (isAutoRefresh) => {
                this.zone.run(() => this.reloadAgent(isAutoRefresh))
            },
            canRefresh: () => true,
        });
        this.noPMLicense = !this.navigationService.hasPartnerPMAccess;
        this.loadDataGames(false);
        this.toggleSeeMore.emit(true); //Set true when enter the see more view
    }

    ngOnDestroy() {
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }
        this.moduleManager?.registerEvents(false);
        this.resizeObserver.disconnect();
    }

    ngAfterViewInit() {
        this.resizeObserver = new ResizeObserver(() => {
            const widgetElement = this.elRef.nativeElement.querySelector('.see-more-container');
            if (widgetElement) {
                const widgetWidth = widgetElement.clientWidth;
                this.isWidgetLarge = widgetWidth > this.maxWithWidget;
                const element = this.totalGamesContainer.nativeElement;
                const elementLabel = this.totalGamesLabel.nativeElement;
                this.isGameContainer = elementLabel.scrollWidth > element.clientWidth;
            }
        });
      
        const widgetElement = this.elRef.nativeElement.querySelector('.see-more-container');
        if (widgetElement) {
            this.resizeObserver.observe(widgetElement);
        }
    }

    @HostListener("window:resize", ["$event"])
    onResize() {
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }

        this.resizeTimeout = setTimeout(() => {
            this.handleResizeEvent();
        }, 500);
        const widgetWidth = document.querySelector('.see-more-container')?.clientWidth;
        this.isWidgetLarge = widgetWidth && widgetWidth > this.maxWithWidget;
    }
    handleResizeEvent() {
        if (this.headerRef && !this.moduleManager.showLoader) {
            this.getScrollSize();
            this.isWidgetSmall = this.moduleManager.getSize().y < 11;
        }
    }

    async loadLocalization() {
        try {
            await this.cvLocalizationService.GetSection("Games_Module");
        } catch {
            console.error("Failed to load localization");
        }
    }

    public getLocalizationString(key: string, defaultValue: string): string {
        const baseString = this.cvLocalizationService.getCachedText("Games_Module", key, defaultValue);
        return baseString;
    }

    async loadDataGames(isAutoRefresh: boolean) {
        if (this.isDataLoading) { return };

        this.isDataLoading = true;
        this.somethingWentWrong = false;

        try {
            setTimeout(() => this.handleResizeEvent(), 3000);
            await this.gamesPromise;
        } catch (err) {
            this.moduleManager.handleLoadError(err, isAutoRefresh);
            this.somethingWentWrong = true;
            return;
        } finally {
            this.finalizeDataLoading();
        }
    }

    finalizeDataLoading() {
        this.getScrollSize();
        this.noData = false;
        this.isDataLoading = false;
    }

    checkTextTruncation(tooltip: string, type: string): string {
        const elements = {
            'name-tooltip': this.agentText,
            'team-tooltip': this.agentTeamText,
            'total-games-tooltip': this.totalGames,
            'total-coins-tooltip': this.totalCoins,
            'total-games-label-tooltip': this.totalGamesLabel,
            'total-coins-label-tooltip': this.totalCoinsLabel
        };

        const element = elements[type];
        if (element && element.nativeElement.scrollWidth !== element.nativeElement.clientWidth) {
            return tooltip;
        }

        return "";
    }

    getScrollSize() {
        let ContainerRef: ElementRef;
        this.supervisorService.getSeeMoreContainer()?.subscribe(container => {
            ContainerRef = container;
        });

        const headerHeight = this.headerRef?.nativeElement?.offsetHeight ?? 0;
        const challengesContainerHeight = ContainerRef?.nativeElement?.offsetHeight ?? 0;
        this.scrollSize = challengesContainerHeight - headerHeight;
    }
    
    retrievedCurrentView() {
        let views;
        this.supervisorService.getCurrentView()?.subscribe(view => {
            views = view;
        });
        return views;
    }

    updatedCurrentView() {
        this.supervisorService.setCurrentView(ViewType.cardView);
        this.toggleSeeMore.emit(false); //Set false when using back button
    }

    private async reloadAgent(isAutoRefresh: boolean) {
        if (!this.noPMLicense) {
            this.isDataLoading = true;
            this.moduleManager.showLoader = true;
            this.somethingWentWrong = false;
            try {
                //Reload just the agent selected information in this view
                let agentResponse = await this.supervisorService.getSupervisorsDataByAgent(this.moduleKey, this.loginId, this.agent.LoginID);
                this.agent = agentResponse; 
                this.agentUpdated.emit(this.agent); //Update the agent information from the see more view
            } catch (err) {
                this.moduleManager.handleLoadError(err, isAutoRefresh);
                this.somethingWentWrong = true;
                return;
            }
            finally {
                this.noData = this.agent === undefined || this.agent === null;
                this.moduleManager.setAutoRefreshReady();
                this.isDataLoading = false;
                this.moduleManager.showLoader = false;
            }
        }
    }
}
